import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Briefcase, Loader2, Eye, Bot, ExternalLink, Pencil, Pause, Play,
  Search, Filter, Check, X, RotateCcw, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { useState, useMemo, useCallback, useEffect } from "react";
import { getCompanyName, isCrawlerJob } from "@/lib/jobUtils";
import { JobQualityBadge } from "@/components/admin/JobQualityBadge";
import { getJobQuality } from "@/lib/jobQuality";
import { toast } from "@/hooks/use-toast";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import { useJobLocations } from "@/hooks/useJobLocations";
import { CityCombobox } from "@/components/admin/CityCombobox";

type StatusFilter = "all" | "publicada" | "pausada";
type QualityFilter = "all" | "baixa" | "media" | "alta";
type SourceFilter = "all" | "manual" | "crawler";
type SortOption = "recent" | "oldest" | "name_asc";

const PAGE_SIZE_OPTIONS = [20, 50, 100, 500] as const;

function qualityLevel(job: any): "baixa" | "media" | "alta" {
  return getJobQuality(job).tier;
}

export default function AdminVagasAtivas() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgs } = useAdminOrgContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [orgFilter, setOrgFilter] = useState("all");
  const [qualityFilter, setQualityFilter] = useState<QualityFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);

  const { states: availableStates, cities: availableCities } = useJobLocations({
    statusIn: ["publicada", "pausada"],
    selectedState: stateFilter,
  });

  // --- Mutual exclusion handlers: search vs geo filters ---
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim() && (stateFilter || cityFilter)) {
      setStateFilter(null);
      setCityFilter(null);
      toast({ title: "Filtros de localidade removidos", description: "A busca pesquisa em todos os campos." });
    }
  };
  const handleStateChange = (value: string) => {
    setStateFilter(value === "all" ? null : value);
    setCityFilter(null);
    if (searchTerm.trim()) {
      setSearchTerm("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setCurrentPage(0);
  };
  const handleCityChange = (value: string | null) => {
    setCityFilter(value);
    if (searchTerm.trim()) {
      setSearchTerm("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setCurrentPage(0);
  };

  // Clear selection on filter/page changes
  useEffect(() => {
    setSelected(new Set());
  }, [currentPage, statusFilter, orgFilter, qualityFilter, sourceFilter, searchTerm, stateFilter, cityFilter]);

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["admin_active_jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select("*, partner_orgs(name, is_auto_created, logo_url), crawler_metadata(source_name, company_name, external_apply_url)")
        .in("status", ["publicada", "pausada"])
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      const { error } = await supabase.from("job_posts").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      const msg = status === "pausada" ? "Vaga pausada" : status === "publicada" ? "Vaga reativada" : "Status atualizado";
      toast({ title: msg });
    },
  });

  const batchUpdate = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      const CHUNK = 200;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const { error } = await supabase.from("job_posts").update(update).in("id", chunk);
        if (error) throw error;
      }
    },
    onSuccess: (_, { ids, status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      setSelected(new Set());
      const labels: Record<string, string> = {
        pausada: "pausadas",
        publicada: "reativadas",
        em_revisao: "devolvidas p/ revisão",
      };
      toast({ title: `${ids.length} vaga(s) ${labels[status] ?? "atualizadas"}` });
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllPage = useCallback((ids: string[]) => {
    setSelected((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  }, []);

  const selectAllFiltered = useCallback((ids: string[]) => {
    setSelected((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  }, []);

  const filteredJobs = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    const filtered = (jobs ?? []).filter((j) => {
      if (search) {
        const company = getCompanyName(j).toLowerCase();
        const haystack = `${j.title} ${company} ${j.location_city ?? ""} ${j.location_state ?? ""}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (statusFilter !== "all" && j.status !== statusFilter) return false;
      if (orgFilter !== "all" && j.org_id !== orgFilter) return false;
      if (qualityFilter !== "all" && qualityLevel(j) !== qualityFilter) return false;
      if (sourceFilter === "crawler" && !isCrawlerJob(j)) return false;
      if (sourceFilter === "manual" && isCrawlerJob(j)) return false;
      if (stateFilter && j.location_state !== stateFilter) return false;
      if (cityFilter && j.location_city !== cityFilter) return false;
      return true;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return new Date(a.published_at ?? a.created_at).getTime() - new Date(b.published_at ?? b.created_at).getTime();
        case "name_asc":
          return a.title.localeCompare(b.title);
        default:
          return new Date(b.published_at ?? b.created_at).getTime() - new Date(a.published_at ?? a.created_at).getTime();
      }
    });

    return filtered;
  }, [jobs, searchTerm, statusFilter, orgFilter, qualityFilter, sourceFilter, sortBy, stateFilter, cityFilter]);

  const jobOrgs = useMemo(() => {
    const map = new Map<string, string>();
    (jobs ?? []).forEach((j) => {
      const orgName = (j as any).partner_orgs?.name;
      if (orgName && !map.has(j.org_id)) map.set(j.org_id, orgName);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [jobs]);

  // Pagination
  const totalPages = Math.ceil(filteredJobs.length / pageSize);
  const paginatedJobs = filteredJobs.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const pageIds = paginatedJobs.map((j) => j.id);
  const allFilteredIds = useMemo(() => {
    const offset = currentPage * pageSize;
    const fromCurrent = filteredJobs.slice(offset);
    const fromStart = offset > 0 ? filteredJobs.slice(0, offset) : [];
    return [...fromCurrent, ...fromStart].slice(0, 500).map((j) => j.id);
  }, [filteredJobs, currentPage, pageSize]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const hasActiveFilters = statusFilter !== "all" || orgFilter !== "all" || qualityFilter !== "all" || sourceFilter !== "all" || sortBy !== "recent" || searchTerm || stateFilter || cityFilter;

  if (isLoading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const activeCount = (jobs ?? []).filter((j) => j.status === "publicada").length;
  const pausedCount = (jobs ?? []).filter((j) => j.status === "pausada").length;

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Vagas Ativas</h1>
            <p className="text-muted-foreground text-sm mt-1">{activeCount} ativas · {pausedCount} pausadas</p>
          </div>
          <div className="relative ml-auto w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por título, empresa, cidade..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <div className="flex gap-3 mt-4 flex-wrap items-center">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filtros:
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setCurrentPage(0); }}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="publicada">Ativa</SelectItem>
            <SelectItem value="pausada">Pausada</SelectItem>
          </SelectContent>
        </Select>
        <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setCurrentPage(0); }}>
          <SelectTrigger className="w-52 h-8 text-xs"><SelectValue placeholder="Organização" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as organizações</SelectItem>
            {jobOrgs.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v as SourceFilter); setCurrentPage(0); }}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as fontes</SelectItem>
            <SelectItem value="crawler">Crawler</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={qualityFilter} onValueChange={(v) => { setQualityFilter(v as QualityFilter); setCurrentPage(0); }}>
          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualquer qualidade</SelectItem>
            <SelectItem value="baixa">Baixa</SelectItem>
            <SelectItem value="media">Média</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stateFilter ?? "all"} onValueChange={handleStateChange}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            {availableStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <CityCombobox cities={availableCities} value={cityFilter} onChange={handleCityChange} />
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v as SortOption); setCurrentPage(0); }}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Mais recentes</SelectItem>
            <SelectItem value="oldest">Mais antigas</SelectItem>
            <SelectItem value="name_asc">Nome A-Z</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <button
            onClick={() => { setStatusFilter("all"); setOrgFilter("all"); setQualityFilter("all"); setSourceFilter("all"); setSortBy("recent"); setSearchTerm(""); setStateFilter(null); setCityFilter(null); setCurrentPage(0); }}
            className="text-xs text-destructive hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Selection controls + page size */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Checkbox checked={allPageSelected} onCheckedChange={() => selectAllPage(pageIds)} />
        <span className="text-xs text-muted-foreground">Selecionar página ({paginatedJobs.length})</span>
        <button
          onClick={() => selectAllFiltered(allFilteredIds)}
          className="text-xs text-primary hover:underline"
        >
          {allFilteredSelected ? "Desmarcar todas" : `Selecionar todas (${Math.min(filteredJobs.length, 500)}) · máx. 500`}
        </button>
        <div className="flex-1" />
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(0); }}>
          <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>{size} / pág</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Batch action bar */}
      {someSelected && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl p-3"
        >
          <span className="text-sm font-medium text-foreground">{selected.size} selecionada(s)</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => batchUpdate.mutate({ ids: [...selected], status: "pausada" })}
            disabled={batchUpdate.isPending}
          >
            <Pause className="w-4 h-4 mr-1" /> Pausar
          </Button>
          <Button
            size="sm"
            onClick={() => batchUpdate.mutate({ ids: [...selected], status: "publicada" })}
            disabled={batchUpdate.isPending}
          >
            <Play className="w-4 h-4 mr-1" /> Reativar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => batchUpdate.mutate({ ids: [...selected], status: "em_revisao" })}
            disabled={batchUpdate.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Devolver p/ revisão
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Limpar
          </Button>
        </motion.div>
      )}

      {/* Table */}
      <div className="mt-6 bg-card rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="p-4 w-10">
                <Checkbox
                  checked={allPageSelected}
                  onCheckedChange={() => selectAllPage(pageIds)}
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground">Vaga</th>
              <th className="text-left p-4 font-medium text-foreground">Organização</th>
              <th className="text-left p-4 font-medium text-foreground">Tipo</th>
              <th className="text-left p-4 font-medium text-foreground">Modelo</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">Publicada em</th>
              <th className="text-right p-4 font-medium text-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
            {paginatedJobs.map((job) => {
              const isCrawler = isCrawlerJob(job);
              const meta = (job as any).crawler_metadata?.[0];
              const isPaused = job.status === "pausada";
              const isSelected = selected.has(job.id);
              return (
                <tr key={job.id} className={`border-b border-border last:border-0 transition-colors ${isSelected ? "bg-primary/5" : "hover:bg-secondary/30"}`}>
                  <td className="p-4">
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(job.id)} />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{job.title}</p>
                      {isCrawler && <Bot className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <JobQualityBadge job={job} />
                    </div>
                    {job.location_city && <p className="text-xs text-muted-foreground">{job.location_city}{job.location_state ? ` - ${job.location_state}` : ""}</p>}
                  </td>
                  <td className="p-4 text-muted-foreground">{getCompanyName(job)}</td>
                  <td className="p-4"><Badge variant="secondary">{job.employment_type}</Badge></td>
                  <td className="p-4 text-muted-foreground">{job.work_model ?? "—"}</td>
                  <td className="p-4">
                    {isPaused
                      ? <Badge variant="outline" className="text-muted-foreground">Pausada</Badge>
                      : <Badge variant="ready">Ativa</Badge>
                    }
                  </td>
                  <td className="p-4 text-muted-foreground text-xs">
                    {job.published_at ? new Date(job.published_at).toLocaleDateString("pt-BR") : "—"}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2" title="Editar" onClick={() => navigate(`/admin/vagas-ativas/${job.id}/editar`)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {isPaused ? (
                        <Button variant="ghost" size="sm" className="h-7 px-2" title="Reativar" disabled={updateStatus.isPending} onClick={() => updateStatus.mutate({ id: job.id, status: "publicada" })}>
                          <Play className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-7 px-2" title="Pausar" disabled={updateStatus.isPending} onClick={() => updateStatus.mutate({ id: job.id, status: "pausada" })}>
                          <Pause className="w-3.5 h-3.5 text-yellow-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 px-2" title="Visualizar" onClick={() => navigate(`/admin/vagas-ativas/${job.id}/editar`)}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {meta?.external_apply_url && (
                        <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                          <a href={meta.external_apply_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Página {currentPage + 1} de {totalPages} · {filteredJobs.length} vagas
          </p>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - 5));
              const p = startPage + i;
              return (
                <Button key={p} size="sm" variant={p === currentPage ? "default" : "ghost"} className="h-7 w-7 px-0 text-xs" onClick={() => setCurrentPage(p)}>
                  {p + 1}
                </Button>
              );
            })}
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {filteredJobs.length === 0 && (
        <div className="bg-muted/50 rounded-2xl p-12 text-center mt-6">
          <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma vaga encontrada</h3>
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters ? "Tente ajustar os filtros." : "Publique vagas pela seção de Revisão de Vagas."}
          </p>
        </div>
      )}
    </div>
  );
}
